import { motion } from "framer-motion";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Rocket, ExternalLink } from "lucide-react";
import { projects } from "../data/portfolioData";

const SubStoreCard = ({ subStore, index }) => (
  <motion.div
    initial={{ y: 50, opacity: 0 }}
    whileInView={{ y: 0, opacity: 1 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.1 }}
  >
    <Card className="modern-card rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] hover:border-rose-500/30 transition-all p-5 h-full group">
      <div className="space-y-3">
        <h4 className="font-semibold text-base text-[hsl(var(--foreground))] group-hover:text-rose-300 transition-colors">
          {subStore.name}
        </h4>
        <p className="text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">
          {subStore.description}
        </p>
        <Button
          asChild
          variant="outline"
          className="w-full rounded-lg border border-[hsl(var(--border))] hover:border-rose-500/30 hover:bg-rose-500/5 text-xs py-2"
        >
          <a href={subStore.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1">
            Visit
            <ExternalLink className="w-3 h-3" />
          </a>
        </Button>
      </div>
    </Card>
  </motion.div>
);

export const StoresSection = ({ onProjectClick }) => {
  const mainStore = projects[0];

  return (
    <section
      id="projects"
      className="py-20 sm:py-24 lg:py-32 bg-[hsl(var(--card))]/30"
      data-testid="projects-section"
    >
      <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-7xl">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" data-testid="projects-title">
            My Store
          </h2>
          <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto" data-testid="projects-subtitle">
            Four specialized shopping destinations under one roof
          </p>
        </motion.div>

        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <Card
            onClick={() => onProjectClick?.(mainStore)}
            role="button"
            tabIndex={0}
            aria-haspopup="dialog"
            aria-label={`Open details for ${mainStore.title}`}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onProjectClick?.(mainStore);
              }
            }}
            data-testid="main-store-card"
            className="modern-card rounded-2xl border border-rose-500/30 hover:border-rose-500/50 bg-gradient-to-br from-[hsl(var(--card))] to-rose-950/20 cursor-pointer transition-all overflow-hidden group focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(var(--background))]"
          >
            <div className="h-1.5 w-full bg-gradient-to-r from-rose-500 via-purple-500 to-rose-500" />
            <div className="p-8 space-y-6">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <h3 className="font-display text-3xl font-bold text-rose-200 group-hover:text-rose-100 transition-colors">
                    RMC Unbound Store
                  </h3>
                  <p className="text-base text-rose-200/70 leading-relaxed max-w-2xl">
                    Four specialized shopping destinations designed to enhance every aspect of your lifestyle. From cutting-edge tech to wellness essentials.
                  </p>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-rose-500/10 border border-rose-500/20 rounded-lg">
                  <div className="w-2 h-2 bg-rose-500 rounded-full animate-pulse" />
                  <span className="font-mono text-sm font-semibold text-rose-500">25</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {mainStore.tags.map((tag) => (
                  <Badge
                    key={tag}
                    className="rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 font-mono text-xs px-3 py-1 transition-all"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>

              <Button
                asChild
                className="modern-button rounded-xl bg-gradient-to-r from-rose-500 to-purple-500 text-white hover:from-rose-600 hover:to-purple-600 font-semibold text-base px-8 py-6 shadow-lg hover:shadow-xl"
              >
                <a
                  href={mainStore.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-2"
                >
                  <Rocket className="w-5 h-5" />
                  Visit Main Store
                </a>
              </Button>
            </div>
          </Card>
        </motion.div>

        <div>
          <h3 className="font-display text-2xl font-bold mb-6 text-center text-[hsl(var(--muted-foreground))]">
            Our Four Specialized Departments
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {mainStore.subStores.map((subStore, index) => (
              <SubStoreCard key={subStore.url} subStore={subStore} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
